import React, { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import {
  Box,
  TextField,
  Button,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  CircularProgress,
  Chip,
  Paper,
  Divider,
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import {
  fetchConversations,
  fetchConversationRequests,
  fetchRequestDetail,
} from 'src/redux/chatbotDashboard'

const formatDate = dateString => {
  if (!dateString) return '-'
  return new Date(dateString).toLocaleString()
}

const MessageRoleLabel = ({ role }) => {
  const roleLabels = {
    User: { label: 'User', color: 'primary' },
    System: { label: 'System', color: 'default' },
    Assistant: { label: 'Assistant', color: 'secondary' },
    // Also support numeric values
    1: { label: 'User', color: 'primary' },
    2: { label: 'System', color: 'default' },
    3: { label: 'Assistant', color: 'secondary' },
  }
  const roleInfo = roleLabels[role] || { label: `Role ${role}`, color: 'default' }
  return <Chip label={roleInfo.label} color={roleInfo.color} size='small' />
}

const StopReasonLabel = ({ stopReason }) => {
  const stopReasons = {
    EndTurn: { label: 'End Turn', color: 'success' },
    ToolUse: { label: 'Tool Use', color: 'secondary' },
    MaxTokens: { label: 'Max Tokens', color: 'warning' },
    StopSequence: { label: 'Stop Sequence', color: 'info' },
    Error: { label: 'Error', color: 'error' },
    // Also support numeric values
    1: { label: 'End Turn', color: 'success' },
    2: { label: 'Tool Use', color: 'secondary' },
    3: { label: 'Max Tokens', color: 'warning' },
    4: { label: 'Stop Sequence', color: 'info' },
    5: { label: 'Error', color: 'error' },
  }
  const info = stopReasons[stopReason] || { label: `Unknown (${stopReason})`, color: 'default' }
  return <Chip label={info.label} color={info.color} size='small' />
}

const ToolCallDisplay = ({ toolCall }) => (
  <Paper sx={{ p: 1, my: 1, bgcolor: 'grey.100' }} elevation={0}>
    <Typography variant='subtitle2' color='primary'>
      Tool Call: {toolCall.name}
    </Typography>
    <Typography variant='caption' color='textSecondary'>
      ID: {toolCall.toolCallId}
    </Typography>
    <Box sx={{ mt: 1 }}>
      <Typography variant='caption' fontWeight='bold'>
        Arguments:
      </Typography>
      <Box
        component='pre'
        sx={{
          fontSize: '0.75rem',
          bgcolor: 'grey.200',
          p: 1,
          borderRadius: 1,
          overflow: 'auto',
          maxHeight: 200,
        }}
      >
        {(() => {
          try {
            return JSON.stringify(JSON.parse(toolCall.arguments), null, 2)
          } catch {
            return toolCall.arguments
          }
        })()}
      </Box>
    </Box>
  </Paper>
)

const ToolResultDisplay = ({ toolResult }) => (
  <Paper
    sx={{ p: 1, my: 1, bgcolor: toolResult.isError ? 'error.light' : 'success.light' }}
    elevation={0}
  >
    <Typography variant='subtitle2' color={toolResult.isError ? 'error' : 'success.dark'}>
      Tool Result {toolResult.isError && '(Error)'}
    </Typography>
    <Typography variant='caption' color='textSecondary'>
      Tool Call ID: {toolResult.toolCallId}
    </Typography>
    <Box
      component='pre'
      sx={{
        fontSize: '0.75rem',
        bgcolor: 'background.paper',
        p: 1,
        borderRadius: 1,
        overflow: 'auto',
        maxHeight: 200,
        mt: 1,
      }}
    >
      {(() => {
        try {
          return JSON.stringify(JSON.parse(toolResult.content), null, 2)
        } catch {
          return toolResult.content
        }
      })()}
    </Box>
  </Paper>
)

const MessageDisplay = ({ message }) => (
  <Paper sx={{ p: 2, my: 1 }} variant='outlined'>
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
      <MessageRoleLabel role={message.role} />
      <Typography variant='caption' color='textSecondary'>
        {formatDate(message.createdAt)}
      </Typography>
    </Box>
    {message.content && (
      <Box
        component='pre'
        sx={{
          fontSize: '0.875rem',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          bgcolor: 'grey.50',
          p: 1,
          borderRadius: 1,
          maxHeight: 300,
          overflow: 'auto',
        }}
      >
        {message.content}
      </Box>
    )}
    {message.toolCalls && message.toolCalls.length > 0 && (
      <Box sx={{ mt: 1 }}>
        {message.toolCalls.map(tc => (
          <ToolCallDisplay key={tc.id} toolCall={tc} />
        ))}
      </Box>
    )}
    {message.toolResults && message.toolResults.length > 0 && (
      <Box sx={{ mt: 1 }}>
        {message.toolResults.map(tr => (
          <ToolResultDisplay key={tr.id} toolResult={tr} />
        ))}
      </Box>
    )}
  </Paper>
)

const RequestDetailView = ({ requestId }) => {
  const dispatch = useDispatch()
  const { requestDetails, requestDetailLoading } = useSelector(state => state.chatbotDashboard)
  const detail = requestDetails[requestId]
  const loading = requestDetailLoading[requestId]

  useEffect(() => {
    if (!detail && !loading) {
      dispatch(fetchRequestDetail(requestId))
    }
  }, [dispatch, requestId, detail, loading])

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
        <CircularProgress size={24} />
      </Box>
    )
  }

  if (!detail) {
    return <Typography color='textSecondary'>Loading...</Typography>
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <Chip label={`Model: ${detail.model}`} variant='outlined' />
        <Chip label={`Tokens: ${detail.tokens || '-'}`} variant='outlined' />
        <Chip label={`Duration: ${detail.durationInMs}ms`} variant='outlined' />
        <StopReasonLabel stopReason={detail.stopReason} />
      </Box>
      {detail.errorMessage && (
        <Paper sx={{ p: 1, mb: 2, bgcolor: 'error.light' }}>
          <Typography color='error' variant='body2'>
            Error: {detail.errorMessage}
          </Typography>
        </Paper>
      )}
      {detail.response && (
        <Box sx={{ mb: 2 }}>
          <Typography variant='subtitle2' gutterBottom>
            Response:
          </Typography>
          <Box
            component='pre'
            sx={{
              fontSize: '0.875rem',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              bgcolor: 'grey.50',
              p: 1,
              borderRadius: 1,
              maxHeight: 200,
              overflow: 'auto',
            }}
          >
            {detail.response}
          </Box>
        </Box>
      )}
      <Divider sx={{ my: 2 }} />
      <Typography variant='subtitle1' gutterBottom>
        Messages ({detail.messages?.length || 0})
      </Typography>
      {detail.messages?.map(msg => <MessageDisplay key={msg.id} message={msg} />)}
    </Box>
  )
}

const RequestAccordion = ({ request }) => {
  const [expanded, setExpanded] = useState(false)

  return (
    <Accordion expanded={expanded} onChange={() => setExpanded(!expanded)}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', width: '100%' }}>
          <Typography variant='body2' fontWeight='bold'>
            Request:
          </Typography>
          <Typography variant='body2' sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
            {request.id}
          </Typography>
          <Chip label={request.model} size='small' variant='outlined' />
          {request.promptName && <Chip label={request.promptName} size='small' />}
          <Chip label={`${request.durationInMs}ms`} size='small' variant='outlined' />
          <StopReasonLabel stopReason={request.stopReason} />
          {request.errorMessage && <Chip label='Error' color='error' size='small' />}
          <Typography variant='caption' color='textSecondary' sx={{ ml: 'auto' }}>
            {formatDate(request.createdAt)}
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails>{expanded && <RequestDetailView requestId={request.id} />}</AccordionDetails>
    </Accordion>
  )
}

const ConversationAccordion = ({ conversation }) => {
  const dispatch = useDispatch()
  const { requestsByConversation, requestsLoading } = useSelector(state => state.chatbotDashboard)
  const [expanded, setExpanded] = useState(false)

  const requests = requestsByConversation[conversation.conversationId]
  const loading = requestsLoading[conversation.conversationId]

  const handleExpand = () => {
    const newExpanded = !expanded
    setExpanded(newExpanded)
    if (newExpanded && !requests && !loading) {
      dispatch(fetchConversationRequests(conversation.conversationId))
    }
  }

  return (
    <Accordion expanded={expanded} onChange={handleExpand}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', width: '100%' }}>
          <Typography variant='body2' fontWeight='bold'>
            Conversation:
          </Typography>
          <Typography variant='body2' sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
            {conversation.conversationId}
          </Typography>
          <Chip label={conversation.chatbotName} size='small' color='primary' />
          <Chip label={`${conversation.requestCount} requests`} size='small' variant='outlined' />
          <Typography variant='body2' color='textSecondary'>
            User: {conversation.userId} ({conversation.userEmail})
          </Typography>
          <Typography variant='caption' color='textSecondary' sx={{ ml: 'auto' }}>
            {formatDate(conversation.firstRequestAt)} - {formatDate(conversation.lastRequestAt)}
          </Typography>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : requests ? (
          <Box sx={{ pl: 2 }}>
            {requests.map(req => (
              <RequestAccordion key={req.id} request={req} />
            ))}
          </Box>
        ) : null}
      </AccordionDetails>
    </Accordion>
  )
}

const ChatbotDashboard = () => {
  const dispatch = useDispatch()
  const { conversations, conversationsLoading } = useSelector(state => state.chatbotDashboard)
  const [userId, setUserId] = useState('')
  const [userEmail, setUserEmail] = useState('')

  useEffect(() => {
    dispatch(fetchConversations({}))
  }, [dispatch])

  const handleSearch = () => {
    dispatch(
      fetchConversations({
        userId: userId || undefined,
        userEmail: userEmail || undefined,
      }),
    )
  }

  const handleKeyPress = e => {
    if (e.key === 'Enter') {
      handleSearch()
    }
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant='h4' gutterBottom>
        Chatbot Dashboard
      </Typography>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant='h6' gutterBottom>
          Filter Conversations
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            label='User ID'
            value={userId}
            onChange={e => setUserId(e.target.value)}
            onKeyPress={handleKeyPress}
            size='small'
            sx={{ width: 150 }}
          />
          <TextField
            label='User Email'
            value={userEmail}
            onChange={e => setUserEmail(e.target.value)}
            onKeyPress={handleKeyPress}
            size='small'
            sx={{ width: 250 }}
          />
          <Button variant='contained' onClick={handleSearch} disabled={conversationsLoading}>
            Search
          </Button>
          <Button
            variant='outlined'
            onClick={() => {
              setUserId('')
              setUserEmail('')
              dispatch(fetchConversations({}))
            }}
            disabled={conversationsLoading}
          >
            Clear
          </Button>
        </Box>
      </Paper>

      {conversationsLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box>
          <Typography variant='subtitle1' gutterBottom>
            Showing {conversations.length} conversation{conversations.length !== 1 ? 's' : ''} (most
            recent 50)
          </Typography>
          {conversations.map(conv => (
            <ConversationAccordion key={conv.conversationId} conversation={conv} />
          ))}
          {conversations.length === 0 && (
            <Typography color='textSecondary'>No conversations found.</Typography>
          )}
        </Box>
      )}
    </Box>
  )
}

export default ChatbotDashboard
